import { api } from '@/api/client'
import type { FiscalStorage, OfdDoc, OfdKkt, Organization } from './types'

export async function ensureOfdAuth(): Promise<boolean> {
  const check = await api<{ isAuthenticated?: boolean }>('/api/ofd-sbis/check-auth')
  if (check.isAuthenticated) return true
  await api('/api/ofd-sbis/auth', { method: 'POST' })
  const again = await api<{ isAuthenticated?: boolean }>('/api/ofd-sbis/check-auth')
  return Boolean(again.isAuthenticated)
}

export async function fetchOrganizations(): Promise<Organization[]> {
  return api<Organization[]>('/api/ofd-sbis/organizations')
}

export async function fetchKktsForInn(inn: string): Promise<OfdKkt[]> {
  return api<OfdKkt[]>(`/api/ofd-sbis/kkts?inn=${encodeURIComponent(inn)}`)
}

export async function fetchKktStorages(inn: string, regId: string): Promise<FiscalStorage[]> {
  const data = await api<{ fiscalStorages?: FiscalStorage[] }>(
    `/api/ofd-sbis/kkt/storages?inn=${encodeURIComponent(inn)}&regId=${encodeURIComponent(regId)}`,
  )
  return data.fiscalStorages || []
}

export async function fetchDocs(params: {
  inn: string
  regId: string
  storageId: string
  dateFrom: string
  dateTo: string
  limit?: number
  shiftNumber?: string
}): Promise<OfdDoc[]> {
  const q = new URLSearchParams({
    inn: params.inn,
    regId: params.regId,
    storageId: params.storageId,
    dateFrom: params.dateFrom,
    dateTo: params.dateTo,
    limit: String(params.limit ?? 2000),
  })
  if (params.shiftNumber) q.set('shiftNumber', params.shiftNumber)
  const data = await api<{ items?: OfdDoc[] }>(`/api/ofd-sbis/docs?${q}`)
  return data.items || []
}

export interface OfdTerminalBinding {
  inn: string
  regId: string
  storageId: string
  fsNumber?: string
  model?: string
  organizationName?: string
  kktSalesPoint?: string
}

export interface OfdTerminalDocsResult {
  binding: OfdTerminalBinding
  period?: { from?: string; to?: string }
  items: OfdDoc[]
  stats: {
    receipt_count: number
    sum_closed: number
    avg_closed: number
  }
}

export async function fetchDocsByTerminal(params: {
  fsNumber?: string | null
  serialNumber?: string | null
  inn?: string | null
  regId?: string | null
  dateFrom?: string
  dateTo?: string
  limit?: number
}): Promise<OfdTerminalDocsResult> {
  const q = new URLSearchParams()
  if (params.fsNumber) q.set('fsNumber', params.fsNumber)
  if (params.serialNumber) q.set('serialNumber', params.serialNumber)
  if (params.inn) q.set('inn', params.inn)
  if (params.regId) q.set('regId', params.regId)
  if (params.dateFrom) q.set('dateFrom', params.dateFrom)
  if (params.dateTo) q.set('dateTo', params.dateTo)
  if (params.limit != null) q.set('limit', String(params.limit))
  return api<OfdTerminalDocsResult>(`/api/ofd-sbis/docs-by-terminal?${q}`)
}

export async function fetchDocByTerminal(params: {
  inn: string
  regId: string
  storageId: string
  docNum: string | number
  docDate: string
}): Promise<Record<string, unknown>> {
  const q = new URLSearchParams({
    inn: params.inn,
    regId: params.regId,
    storageId: params.storageId,
    docNum: String(params.docNum),
    docDate: params.docDate,
    format: 'json',
  })
  return api(`/api/ofd-sbis/doc-by-terminal?${q}`)
}

export async function fetchDocById(params: {
  inn: string
  regId: string
  storageId: string
  docNum: string | number
  docDate: string
  format?: 'json' | 'pdf'
}): Promise<unknown> {
  const q = new URLSearchParams({
    inn: params.inn,
    regId: params.regId,
    storageId: params.storageId,
    docNum: String(params.docNum),
    docDate: params.docDate,
    format: params.format || 'json',
  })
  if (params.format === 'pdf') {
    const res = await fetch(`/api/ofd-sbis/doc/by-id?${q}`, { credentials: 'include' })
    if (!res.ok) throw new Error('Ошибка загрузки PDF')
    return res.blob()
  }
  return api(`/api/ofd-sbis/doc/by-id?${q}`)
}

export async function fetchDocByQr(params: {
  storageId: string
  docNum: string
  fiscalSign: string
  docDate: string
}): Promise<Record<string, unknown>> {
  const q = new URLSearchParams(params)
  return api(`/api/ofd-sbis/doc/by-qr?${q}`)
}

export async function fetchCheckLink(params: {
  regId: string
  fiscalSign: string
  docDate: string
}): Promise<{ url?: string }> {
  const q = new URLSearchParams(params)
  return api(`/api/ofd-sbis/doc/check-link?${q}`)
}

export async function downloadOfdFile(
  path: string,
  method: 'POST',
  body: unknown,
  fileName: string,
): Promise<void> {
  const res = await fetch(`/api/ofd-sbis${path}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error((data as { error?: string }).error || res.statusText)
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.click()
  URL.revokeObjectURL(url)
}
